import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useLocation as useRouterLocation } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";
import Cache from "../utils/cache";
import queryString from "query-string";

const ITEMS_PER_PAGE = 20;

export default function DollarDealsPage() {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState(() => {
    const params = queryString.parse(window.location.search);
    return typeof params.sort === "string" ? params.sort : "newest";
  });
  const initialPageSetRef = useRef(false);
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const prevSortOrderRef = useRef(null);


  // Sync page and sortOrder from URL on initial load
  useEffect(() => {
    if (initialPageSetRef.current) return;

    const params = queryString.parse(routerLocation.search);
    const pageFromUrl = parseInt(params.page || "1", 10);
    const sortFromUrl = typeof params.sort === "string" ? params.sort : "newest";

    if (!isNaN(pageFromUrl) && pageFromUrl > 0) {
      setCurrentPage(pageFromUrl);
    }

    // ✅ Sync both sortOrder and prevSortOrderRef correctly
    setSortOrder(sortFromUrl);
    prevSortOrderRef.current = sortFromUrl;

    initialPageSetRef.current = true;
  }, [routerLocation.search]);




  // Update URL when page or sortOrder changes
  useEffect(() => {
    const params = queryString.parse(routerLocation.search);
    let needUpdate = false;

    if (parseInt(params.page || "1", 10) !== currentPage) {
      params.page = currentPage;
      needUpdate = true;
    }
    if ((params.sort || "newest") !== sortOrder) {
      params.sort = sortOrder;
      needUpdate = true;
    }

    if (needUpdate) {
      const newSearch = queryString.stringify(params);
      navigate({ search: newSearch }, { replace: true });
    }
  }, [currentPage, sortOrder, navigate, routerLocation.search]);

  useEffect(() => {
    if (!initialPageSetRef.current) return;

    const prevSortOrder = prevSortOrderRef.current;
    if (prevSortOrder !== sortOrder) {
      console.log(`Sort order changed from "${prevSortOrder}" to "${sortOrder}". Resetting page from ${currentPage} to 1.`);
      prevSortOrderRef.current = sortOrder; // sync for next change
      setCurrentPage(1);
    }
  }, [sortOrder, currentPage]);



  // Load offers from cache or Firestore
  useEffect(() => {
    async function loadOffers() {
      setLoading(true);
      setError(null);
      try {
        const cached = Cache.getData() || {};
        let productOffers = cached.productOffers || [];

        if (!productOffers.length) {
          const snapshot = await getDocs(collection(db, "product_offers"));
          productOffers = [];
          snapshot.docs.forEach((doc) => {
            const data = doc.data();
            if (Array.isArray(data.offers)) {
              productOffers.push(
                ...data.offers.map((offer) => ({
                  ...offer,
                  id:
                    offer.id ||
                    `${doc.id}_${offer.offerId || Math.random().toString(36).slice(2, 8)}`,
                  uploadedDttm: offer.uploadedDttm || data.uploadedDttm || null,
                }))
              );
            }
          });
          Cache.setData({ ...cached, productOffers });
        }

        const now = new Date();
        const filtered = productOffers.filter((offer) => {
          const price = parseFloat(offer.offerPrice);
          return (
            price > 0 &&
            price <= 1 &&
            (!offer.endDttm || offer.endDttm.toDate() > now) &&
            (!offer.startDttm || offer.startDttm.toDate() <= now)
          );
        });

        setOffers(filtered);
      } catch (err) {
        setError("Failed to load $1 deals: " + err.message);
      } finally {
        setLoading(false);
      }
    }

    loadOffers();
  }, []);

  // Sort offers according to sortOrder
  const sortedOffers = useMemo(() => {
    const copy = [...offers];
    copy.sort((a, b) => {
      const getDate = (o) => (o.uploadedDttm?.toDate ? o.uploadedDttm.toDate() : new Date(0));
      const getPrice = (o) =>
        Number(o.originalPrice) || Number(o.price) || Number(o.offerPrice) || 999999;

      switch (sortOrder) {
        case "newest":
          return getDate(b) - getDate(a);
        case "oldest":
          return getDate(a) - getDate(b);
        case "priceLow":
          return getPrice(a) - getPrice(b);
        case "priceHigh":
          return getPrice(b) - getPrice(a);
        default:
          return 0;
      }
    });
    return copy;
  }, [offers, sortOrder]);

  // Pagination calculations
  const totalPages = Math.max(1, Math.ceil(sortedOffers.length / ITEMS_PER_PAGE));
  const paginated = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedOffers.slice(start, start + ITEMS_PER_PAGE);
  }, [sortedOffers, currentPage]);

  // Clamp currentPage if out of bounds
  useEffect(() => {
    if (currentPage < 1) {
      setCurrentPage(1);
      console.log("Here -0");
    } else if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // Rebate popup handlers
  const handleRebateClick = (offer) => {
    setSelectedProduct(offer);
    setShowPopup(true);
  };
  const closePopup = () => {
    setShowPopup(false);
    setSelectedProduct(null);
  };

  const onSortChange = (newSort) => {
    if (newSort !== sortOrder) {
      setSortOrder(newSort);
      console.log(newSort);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6 text-center text-indigo-700 drop-shadow-md tracking-wide">Dollar Deals ($1 or Less)</h1>

      <div className="mb-4 flex justify-end items-center gap-4">
        <label htmlFor="sortOrder" className="font-semibold">
          Sort By:
        </label>
        <select
          id="sortOrder"
          value={sortOrder}
          onChange={(e) => onSortChange(e.target.value)}
          className="border border-gray-300 rounded px-2 py-1"
        >
          <option value="newest">Newest</option>
          <option value="oldest">Oldest</option>
          <option value="priceLow">Price: Low to High</option>
          <option value="priceHigh">Price: High to Low</option>
        </select>
      </div>

      {loading ? (
        <div className="text-center text-gray-600">Loading dollar deals...</div>
      ) : error ? (
        <div className="text-center text-red-600">{error}</div>
      ) : paginated.length === 0 ? (
        <div className="text-center text-gray-700 mt-10">
          <p className="text-xl font-medium">😞 No $1 deals available right now.</p>
          <p className="text-sm text-gray-500 mt-2">Please check back soon.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
          {paginated.map((offer) => (
            <div
              key={offer.id}
              className="relative border rounded-xl p-4 shadow hover:shadow-lg transition bg-white flex flex-col justify-between cursor-pointer"
              onClick={() => navigate(`/product/${offer.id}?page=${currentPage}&sort=${sortOrder}`)}
            >
              {offer.rebateAmount > 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRebateClick(offer);
                  }}
                  className="absolute top-2 right-2 bg-red-100 px-2 py-1 rounded text-red-700 font-bold animate-pulse text-xs shadow"
                >
                  Rebate Offer
                </button>
              )}

              {offer.offerImage && (
                <img
                  src={offer.offerImage}
                  alt={offer.productName}
                  className="h-40 w-full object-contain mb-2"
                />
              )}

              <h2 className="text-md font-bold line-clamp-2">{offer.productName}</h2>
              <p className="text-sm text-gray-600 line-clamp-2 mb-2">
                {offer.description}
              </p>

              <div className="flex items-baseline space-x-2 mb-1">
                <span className="text-indigo-700 font-bold text-md">
                  ${parseFloat(offer.offerPrice).toFixed(2)}
                </span>
                {offer.originalPrice && (
                  <span className="text-xs text-gray-500 line-through">
                    ${parseFloat(offer.originalPrice).toFixed(2)}
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-2 text-xs mt-1">
                {(offer.category || (Array.isArray(offer.tags) && offer.tags.length > 0)) && (
                  <p className="mb-1 text-xs text-gray-600">
                    {offer.category && <>Category: {offer.category}<br /></>} 
                  </p>
                )}
                {offer.offerType && (
                  <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded">{offer.offerType}</span>
                )}
                {offer.freeStorePickup?.toLowerCase() === "yes" && (
                  <p className="mb-1 text-green-600 font-semibold text-xs">🚚 Free Ship to Store</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {paginated.length > 0 && (
        <div className="flex justify-center mt-6 space-x-3">
          <button
            className="px-3 py-1 border rounded disabled:opacity-50"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </button>
          <span className="px-3 py-1 border rounded">
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="px-3 py-1 border rounded disabled:opacity-50"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          >
            Next
          </button>
        </div>
      )}

      {showPopup && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md shadow-lg text-center relative">
            <h2 className="text-lg font-semibold mb-2">📋 Rebate Required</h2>
            <p className="mb-4">
              This offer requires a rebate application to reduce the price to{" "}
              <strong>${selectedProduct.offerPrice}</strong>.
            </p>
            <button
              onClick={closePopup}
              className="mt-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Got It
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
